import { Box, Typography } from '@mui/material';
import { memo, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageCarousel } from '../Image/ImageCarousel';
import { AuxCaptureInfo, useTrainAuxImage, useTrainAuxImageCount } from '../../api/queries/trainAuxCaptures';

interface AuxImageCarouselProps {
  auxCapture: AuxCaptureInfo;
  jobsiteId: string;
  recordingId: string;
}

export const TrainAuxImagesCarousel = memo(function ({
  auxCapture,
  jobsiteId,
  recordingId,
}: AuxImageCarouselProps): ReactElement {
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  // Get image count for this aux capture
  const imageCountData = useTrainAuxImageCount({ jobsiteId, recordingId, auxIndex: auxCapture.auxIndex.toString() });
  const imageCount = imageCountData.data || 0;

  const handlePrevious = () => {
    setCurrentImageIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentImageIndex((prev) => Math.min(imageCount - 1, prev + 1));
  };

  // Always call the hook but only use the result when needed
  const { data: currentImageData } = useTrainAuxImage({
    jobsiteId,
    recordingId,
    auxIndex: auxCapture.auxIndex,
    imageIndex: currentImageIndex,
  });

  const renderImageContent = () => {
    if (!currentImageData) {
      return <Box display='flex' alignItems='center' justifyContent='center' height='200px' color='text.secondary' />;
    }

    if (imageCount === 0) {
      return (
        <Box display='flex' alignItems='center' justifyContent='center' height='200px' color='text.secondary'>
          <Typography variant='body2'>{t('recording.auxiliary.noImagesAvailable')}</Typography>
        </Box>
      );
    }

    const imageKey = `${auxCapture.auxIndex}-${currentImageIndex}`;
    const hasImageError = imageErrors.has(imageKey);

    if (hasImageError) {
      return (
        <Box display='flex' alignItems='center' justifyContent='center' height='200px' color='text.secondary'>
          <Typography variant='body2'>{t('recording.auxiliary.imageLoadingError')}</Typography>
        </Box>
      );
    }

    return (
      <Box display='flex' justifyContent='center' alignItems='center' height='200px'>
        <img
          key={imageKey}
          src={currentImageData}
          alt={`${t(auxCapture.label)} auxiliary capture ${currentImageIndex + 1}`}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
          }}
          onLoad={() => {
            setImageErrors((prev) => {
              const newErrors = new Set(prev);
              newErrors.delete(imageKey);
              return newErrors;
            });
          }}
          onError={() => {
            setImageErrors((prev) => new Set([...prev, imageKey]));
          }}
        />
      </Box>
    );
  };

  return (
    <Box display='flex' flexDirection='column' alignItems='center' width='100%' gap={2}>
      <Typography variant='subtitle2' gutterBottom color='text.secondary'>
        {t(auxCapture.label)}
      </Typography>

      <ImageCarousel
        currentIndex={currentImageIndex}
        totalCount={imageCount}
        onPrevious={handlePrevious}
        onNext={handleNext}
        hasZoom={true}
      >
        {renderImageContent()}
      </ImageCarousel>
    </Box>
  );
});
